import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  QcCommentsByQuestionId,
  QcFirstPassNote,
  QcIssue,
  QcIssueAuthor,
  QcIssueReply,
  QcIssueCategory,
  QcIssuesByQuestionId,
  QcIssueStatus,
} from '../../interfaces/qcIssue.interface';

// Workspace UI/session state: QC data seeds from services/qcReview and views persist their optimistic writes; filters stay session-only.

export interface AssessmentWorkspaceState {
  qcOnlyFilter: boolean;
  incompleteOnlyFilter: boolean;
  tagFilters: string[];
  statusFilters: QcIssueStatus[];
  categoryFilters: QcIssueCategory[];
  spAnswerFilters: string[];
  assessorAnswerFilters: string[];
  questionTypeFilters: string[];
  qcIssuesByQuestion: QcIssuesByQuestionId;
  qcNotesByQuestion: Record<number, QcFirstPassNote>;
  // Questions whose First Pass draft was rejected; their First Pass note stays hidden.
  qcRejectedDraftQuestionIds: number[];
  qcCommentsByQuestion: QcCommentsByQuestionId;
  qcIssuesSeeded: boolean;
  qcReviewSubmitted: boolean;
  qcRoundNumber: number;
  qcRoundClosed: boolean;
  qcSubmittedAt?: number;
  qcApprovalDismissed: boolean;
  qcAssessmentApproved: boolean;
  // Temporary ids for optimistic creations, replaced by server ids via replaceQcIssue/replaceQcReply when the POST resolves.
  nextQcId: number;
  sessionSaves: Record<number, { by: string; at: number }>;
}

const initialState: AssessmentWorkspaceState = {
  qcOnlyFilter: false,
  incompleteOnlyFilter: false,
  tagFilters: [],
  statusFilters: [],
  categoryFilters: [],
  spAnswerFilters: [],
  assessorAnswerFilters: [],
  questionTypeFilters: [],
  qcIssuesByQuestion: {},
  qcNotesByQuestion: {},
  qcRejectedDraftQuestionIds: [],
  qcCommentsByQuestion: {},
  qcIssuesSeeded: false,
  qcReviewSubmitted: false,
  qcRoundNumber: 1,
  qcRoundClosed: false,
  qcApprovalDismissed: false,
  qcAssessmentApproved: false,
  nextQcId: 900000000,
  sessionSaves: {},
};

const assessmentWorkspaceSlice = createSlice({
  name: 'assessmentWorkspace',
  initialState,
  reducers: {
    // One header tile can stand for several statuses; on means any of them is filtered, so off clears them all.
    toggleStatusFilter: (state, action: PayloadAction<QcIssueStatus[]>) => {
      const statuses = action.payload;
      const anySelected = statuses.some(status => state.statusFilters.includes(status));
      if (anySelected) {
        state.statusFilters = state.statusFilters.filter(status => !statuses.includes(status));
      } else {
        statuses.forEach(status => state.statusFilters.push(status));
      }
    },
    setQcOnlyFilter: (state, action: PayloadAction<boolean>) => {
      state.qcOnlyFilter = action.payload;
    },
    // Deep-link seeding: replaces the whole filter selection in one dispatch (URL params are load-time truth).
    seedWorkspaceFilters: (state, action: PayloadAction<{
      statusFilters: QcIssueStatus[];
      categoryFilters: QcIssueCategory[];
      tagFilters: string[];
      spAnswerFilters: string[];
      assessorAnswerFilters: string[];
      questionTypeFilters: string[];
      qcOnlyFilter: boolean;
      incompleteOnlyFilter: boolean;
    }>) => {
      state.statusFilters = action.payload.statusFilters;
      state.categoryFilters = action.payload.categoryFilters;
      state.tagFilters = action.payload.tagFilters;
      state.spAnswerFilters = action.payload.spAnswerFilters;
      state.assessorAnswerFilters = action.payload.assessorAnswerFilters;
      state.questionTypeFilters = action.payload.questionTypeFilters;
      state.qcOnlyFilter = action.payload.qcOnlyFilter;
      state.incompleteOnlyFilter = action.payload.incompleteOnlyFilter;
    },
    clearStatusFilters: (state) => {
      state.statusFilters = [];
      state.categoryFilters = [];
      state.tagFilters = [];
      state.spAnswerFilters = [];
      state.assessorAnswerFilters = [];
      state.questionTypeFilters = [];
      state.qcOnlyFilter = false;
      state.incompleteOnlyFilter = false;
    },
    seedQcIssues: (state, action: PayloadAction<QcIssuesByQuestionId>) => {
      state.qcIssuesByQuestion = action.payload;
      state.qcIssuesSeeded = true;
    },
    seedQcNotes: (state, action: PayloadAction<Record<number, QcFirstPassNote>>) => {
      state.qcNotesByQuestion = action.payload;
    },
    setQcNoteResponse: (state, action: PayloadAction<{ questionId: number; adminResponse: 'accept' | 'reject'; note?: string }>) => {
      const existing = state.qcNotesByQuestion[action.payload.questionId];
      if (!existing) return;
      existing.adminResponse = action.payload.adminResponse;
      existing.adminResponseNote = action.payload.note || '';
    },
    seedQcRejectedDrafts: (state, action: PayloadAction<number[]>) => {
      state.qcRejectedDraftQuestionIds = action.payload;
    },
    markQcDraftRejected: (state, action: PayloadAction<number>) => {
      if (state.qcRejectedDraftQuestionIds.includes(action.payload)) return;
      state.qcRejectedDraftQuestionIds.push(action.payload);
    },
    seedQcComments: (state, action: PayloadAction<QcCommentsByQuestionId>) => {
      state.qcCommentsByQuestion = action.payload;
    },
    setQcIssueStatus: (state, action: PayloadAction<{ questionId: number; issueId: number; status: QcIssueStatus; by: string }>) => {
      const issues = state.qcIssuesByQuestion[action.payload.questionId];
      const issue = issues?.find(i => i.id === action.payload.issueId);
      if (!issue) return;
      issue.status = action.payload.status;
      issue.statusBy = action.payload.by;
      issue.statusAt = new Date().toISOString();
    },
    // `id` lets an optimistic add pick a temp id to reconcile later via replaceQcIssue.
    addQcIssue: (state, action: PayloadAction<{
      questionId: number;
      category: QcIssueCategory;
      text: string;
      author: QcIssueAuthor;
      id?: number;
    }>) => {
      const { questionId, category, text, author } = action.payload;
      let { id } = action.payload;
      if (id === undefined) {
        state.nextQcId += 1;
        id = state.nextQcId;
      }
      const issues = state.qcIssuesByQuestion[questionId] ?? [];
      state.qcIssuesByQuestion[questionId] = [...issues, {
        id,
        question: questionId,
        status: 'pending_assessor_action',
        category,
        text,
        author,
        createdAt: new Date().toISOString(),
        replies: [],
      }];
    },
    replaceQcIssue: (state, action: PayloadAction<{ questionId: number; issueId: number; issue: QcIssue }>) => {
      const issues = state.qcIssuesByQuestion[action.payload.questionId];
      if (!issues) return;
      const index = issues.findIndex(i => i.id === action.payload.issueId);
      if (index === -1) return;
      issues[index] = action.payload.issue;
    },
    // Accepting a First Pass draft publishes it as the accepting admin's issue (acceptedFromAi keeps provenance).
    acceptQcIssue: (state, action: PayloadAction<{
      questionId: number;
      issueId: number;
      author: QcIssueAuthor;
    }>) => {
      const issue = state.qcIssuesByQuestion[action.payload.questionId]?.find(i => i.id === action.payload.issueId);
      if (!issue || issue.status !== 'first_pass') return;
      issue.status = 'pending_assessor_action';
      issue.author = action.payload.author;
      issue.acceptedFromAi = true;
      issue.statusBy = action.payload.author.name;
      issue.statusAt = new Date().toISOString();
    },
    updateQcIssue: (state, action: PayloadAction<{
      questionId: number;
      issueId: number;
      category: QcIssueCategory;
      text: string;
    }>) => {
      const issue = state.qcIssuesByQuestion[action.payload.questionId]?.find(i => i.id === action.payload.issueId);
      if (!issue) return;
      issue.category = action.payload.category;
      issue.text = action.payload.text;
    },
    deleteQcIssue: (state, action: PayloadAction<{ questionId: number; issueId: number }>) => {
      const issues = state.qcIssuesByQuestion[action.payload.questionId];
      if (!issues) return;
      state.qcIssuesByQuestion[action.payload.questionId] = issues.filter(i => i.id !== action.payload.issueId);
    },
    // `id` mirrors addQcIssue: temp id from optimistic callers, swapped via replaceQcReply.
    addQcReply: (state, action: PayloadAction<{
      questionId: number;
      issueId: number;
      text: string;
      author: QcIssueAuthor;
      id?: number;
    }>) => {
      const issue = state.qcIssuesByQuestion[action.payload.questionId]?.find(i => i.id === action.payload.issueId);
      if (!issue) return;
      let { id } = action.payload;
      if (id === undefined) {
        state.nextQcId += 1;
        id = state.nextQcId;
      }
      issue.replies = [...issue.replies, {
        id,
        author: action.payload.author,
        text: action.payload.text,
        createdAt: new Date().toISOString(),
      }];
    },
    replaceQcReply: (state, action: PayloadAction<{
      questionId: number;
      issueId: number;
      replyId: number;
      reply: QcIssueReply;
    }>) => {
      const issue = state.qcIssuesByQuestion[action.payload.questionId]?.find(i => i.id === action.payload.issueId);
      if (!issue) return;
      const index = issue.replies.findIndex(r => r.id === action.payload.replyId);
      if (index === -1) return;
      issue.replies[index] = action.payload.reply;
    },
    updateQcReply: (state, action: PayloadAction<{ questionId: number; issueId: number; replyId: number; text: string }>) => {
      const issue = state.qcIssuesByQuestion[action.payload.questionId]?.find(i => i.id === action.payload.issueId);
      const reply = issue?.replies.find(r => r.id === action.payload.replyId);
      if (!reply) return;
      reply.text = action.payload.text;
    },
    deleteQcReply: (state, action: PayloadAction<{ questionId: number; issueId: number; replyId: number }>) => {
      const issue = state.qcIssuesByQuestion[action.payload.questionId]?.find(i => i.id === action.payload.issueId);
      if (!issue) return;
      issue.replies = issue.replies.filter(r => r.id !== action.payload.replyId);
    },
    addQcComment: (state, action: PayloadAction<{ questionId: number; text: string; author: QcIssueAuthor }>) => {
      state.nextQcId += 1;
      const comments = state.qcCommentsByQuestion[action.payload.questionId] ?? [];
      state.qcCommentsByQuestion[action.payload.questionId] = [...comments, {
        id: state.nextQcId,
        author: action.payload.author,
        text: action.payload.text,
        createdAt: new Date().toISOString(),
      }];
    },
    submitQcReview: (state) => {
      state.qcReviewSubmitted = true;
      state.qcSubmittedAt = Date.now();
    },
    // Server truth on load: qcReviewSubmitted derives from the cycle's submittedAt.
    seedQcCycle: (state, action: PayloadAction<{
      submittedAt: string | null;
      roundNumber?: number;
      roundClosedAt?: string | null;
      approvedAt?: string | null;
    } | null>) => {
      const submittedAt = action.payload?.submittedAt ?? null;
      state.qcReviewSubmitted = !!submittedAt;
      state.qcSubmittedAt = submittedAt ? Date.parse(submittedAt) : 0;
      state.qcRoundNumber = action.payload?.roundNumber ?? 1;
      state.qcRoundClosed = !!action.payload?.roundClosedAt;
      // An approved cycle keeps the approval banner/state across reloads.
      const approved = !!action.payload?.approvedAt;
      state.qcAssessmentApproved = approved;
      if (approved) state.qcApprovalDismissed = true;
    },
    dismissQcApproval: (state) => {
      state.qcApprovalDismissed = true;
    },
    reopenQcApproval: (state) => {
      state.qcApprovalDismissed = false;
    },
    recordSessionSave: (state, action: PayloadAction<{ questionId: number; by: string; at: number }>) => {
      state.sessionSaves[action.payload.questionId] = { by: action.payload.by, at: action.payload.at };
    },
    resetAssessmentWorkspace: () => initialState,
  },
});

export const {
  toggleStatusFilter,
  setQcOnlyFilter,
  seedWorkspaceFilters,
  clearStatusFilters,
  seedQcIssues,
  seedQcNotes,
  setQcNoteResponse,
  seedQcRejectedDrafts,
  markQcDraftRejected,
  seedQcComments,
  setQcIssueStatus,
  addQcIssue,
  replaceQcIssue,
  acceptQcIssue,
  updateQcIssue,
  deleteQcIssue,
  addQcReply,
  replaceQcReply,
  updateQcReply,
  deleteQcReply,
  addQcComment,
  submitQcReview,
  seedQcCycle,
  dismissQcApproval,
  reopenQcApproval,
  recordSessionSave,
  resetAssessmentWorkspace,
} = assessmentWorkspaceSlice.actions;

export default assessmentWorkspaceSlice.reducer;
